// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * "Fully VR" (owner, 2026-10-22; docs/AYNI_XR_SATELLITES_PLAN.md): an
 * opaque sky around the viewer that hides the passthrough room — the Milky
 * Way and the stars from NASA's Deep Star Map, the bright stars of the
 * Yale catalogue as crisp points, and the Sun as a disc with a glow.
 *
 * AYNI (owner, 2026-11-03): floating in space. The sky is drawn all the way
 * round, with no ground and no horizon (it used to stand from the viewer's
 * place, with dark ground under the horizon), and it is tied to the globe,
 * not to the room: the caller passes the globe's turn times skyMath
 * `celestialToGlobeLocal`, so the celestial pole lies along the globe's
 * axis, the Sun stands where the globe's lit side faces, and turning the
 * globe turns the stars with it. The sky turns with sidereal time (and
 * with the clock's speeds and scrubbing), so the Sun and the galaxy move as
 * they do.
 *
 * Frames: the star map and the catalogue are in J2000 equatorial
 * coordinates (right ascension, declination). Everything here lives in
 * that inertial frame; one matrix per frame turns the group into the room,
 * so the GPU does the rest.
 *
 * Sources (both same-origin, public/assets/sky, plain git blobs):
 * - starmap_4k.jpg — NASA/Goddard SVS "Deep Star Maps" (svs.gsfc.nasa.gov/3895),
 *   4096×2048, RA 0h at the centre, RA increasing to the left; NASA media,
 *   public domain, credit asked for.
 * - stars.json — Yale Bright Star Catalogue 5 (Hoffleit & Warren 1991, VizieR V/50):
 *   9,096 stars [raDeg, decDeg, vmag, B−V], brightest first.
 */
import type * as THREE from 'three'
import { apply3, celestialToGlobeLocal, eciDirection, gmst, multiply3, sunRaDec } from './satellites/skyMath'

/**
 * AYNI (Fully VR as floating in space, owner 2026-11-03): the sky's turn
 * (row-major 3×3, celestial → room) for a globe turned by `globeQ` (its
 * quaternion, with the zoom's fold) at `timeMs`: the globe's turn times
 * the Earth's turn among the stars. `scratch` is any Matrix4 to work in.
 */
export function skyTurnForGlobe(scratch: THREE.Matrix4, globeQ: THREE.Quaternion, timeMs: number): number[] {
  const e = scratch.makeRotationFromQuaternion(globeQ).elements
  // three's elements are column-major; skyMath's matrices are row-major.
  const globeTurn = [e[0], e[4], e[8], e[1], e[5], e[9], e[2], e[6], e[10]]
  return multiply3(globeTurn, celestialToGlobeLocal(gmst(timeMs)))
}

/** The sky's radius, metres: outside the satellites' 25 m dome, inside the camera's far plane (100 m). */
export const NIGHT_SKY_M = 40
export const STARMAP_URL = '/assets/sky/starmap_4k.jpg'
export const STARS_URL = '/assets/sky/stars.json'

export interface VrNightSkyHandle {
  readonly group: THREE.Group
  /** The star map has arrived (the sky is drawn only then). */
  readonly ready: boolean
  /** The bright stars that are drawn, for checks. */
  readonly starCount: number
  /**
   * Per XR frame. `on` with the sky's turn (row-major 3×3, celestial →
   * room: the globe's turn times `celestialToGlobeLocal`) draws the sky;
   * anything else hides it. Loading starts on the first `on`.
   */
  update(on: boolean, celestialToRoom: readonly number[] | null, timeMs: number, viewportHeightPx: number): void
  /** Where a celestial direction stands on the sky right now (unit, room space), for checks. */
  domeDirection(raRad: number, decRad: number): [number, number, number] | null
  dispose(): void
}

export interface VrNightSkyOptions {
  fetcher?: typeof fetch
  starmapUrl?: string
  starsUrl?: string
}

const SKY_VERT = /* glsl */ `
varying vec3 vDir;
void main() {
  vDir = position;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`

const SKY_FRAG = /* glsl */ `
uniform sampler2D uMap;
uniform float uHasMap;
uniform vec3 uSun;
varying vec3 vDir;
const float PI = 3.141592653589793;
void main() {
  vec3 d = normalize(vDir);
  // The map: RA 0h at the centre, RA increasing to the left; declination up.
  float ra = atan(d.y, d.x);
  float dec = asin(clamp(d.z, -1.0, 1.0));
  vec2 uv = vec2(fract((PI - ra) / (2.0 * PI)), (dec + PI * 0.5) / PI);
  // The map is exposed for print; lifted a little so the Milky Way reads on a headset.
  vec3 sky = uHasMap > 0.5 ? pow(texture2D(uMap, uv).rgb * 1.5, vec3(0.9)) : vec3(0.0);
  // The Sun: a disc of 0.53 degrees across and a glow around it.
  float ang = acos(clamp(dot(d, uSun), -1.0, 1.0));
  float disc = 1.0 - smoothstep(0.0042, 0.0052, ang);
  float glow = exp(-ang * 14.0) * 0.8 + exp(-ang * 2.2) * 0.1;
  sky += vec3(1.0, 0.97, 0.9) * (disc * 3.0 + glow);
  // Space: the sky all the way round, no ground and no horizon (owner, 2026-11-03).
  gl_FragColor = vec4(sky, 1.0);
}`

const STAR_VERT = /* glsl */ `
attribute float aMag;
attribute vec3 aColor;
uniform float uScale;
varying float vAlpha;
varying vec3 vColor;
void main() {
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  gl_PointSize = clamp(7.5 - aMag * 1.1, 1.3, 11.0) * uScale;
  // Every star, in every direction (no horizon in space).
  vAlpha = clamp(1.0 - (aMag - 3.5) * 0.25, 0.35, 1.0);
  vColor = aColor;
}`

const STAR_FRAG = /* glsl */ `
varying float vAlpha;
varying vec3 vColor;
void main() {
  vec2 c = gl_PointCoord - 0.5;
  float r2 = dot(c, c);
  if (r2 > 0.25 || vAlpha <= 0.0) discard;
  gl_FragColor = vec4(vColor, vAlpha * smoothstep(0.25, 0.06, r2));
}`

/** A star's colour from its B−V index: blue-white to orange. */
export function starColour(bv: number): [number, number, number] {
  const t = Math.max(0, Math.min(1, (bv + 0.3) / 1.8))
  return [0.72 + 0.28 * t, 0.84 + 0.16 * (1 - Math.abs(t - 0.4) / 0.6), 1 - 0.45 * t]
}

type StarRow = [number, number, number, number]

interface StarsFile {
  stars: StarRow[]
}

export function createVrNightSky(THREE_: typeof THREE, parent: THREE.Object3D, opts: VrNightSkyOptions = {}): VrNightSkyHandle {
  const group = new THREE_.Group()
  group.name = 'night-sky'
  group.visible = false
  group.matrixAutoUpdate = false
  parent.add(group)

  const skyUniforms = {
    uMap: { value: null as THREE.Texture | null },
    uHasMap: { value: 0 },
    uSun: { value: new THREE_.Vector3(1, 0, 0) },
  }
  const skyMat = new THREE_.ShaderMaterial({
    uniforms: skyUniforms,
    vertexShader: SKY_VERT,
    fragmentShader: SKY_FRAG,
    side: THREE_.BackSide,
    depthWrite: true,
    depthTest: true,
  })
  skyMat.toneMapped = false
  const skyGeo = new THREE_.SphereGeometry(NIGHT_SKY_M, 48, 24)
  const sky = new THREE_.Mesh(skyGeo, skyMat)
  sky.frustumCulled = false
  sky.renderOrder = -10
  group.add(sky)

  const starUniforms = {
    uScale: { value: 1 },
  }
  const starMat = new THREE_.ShaderMaterial({
    uniforms: starUniforms,
    vertexShader: STAR_VERT,
    fragmentShader: STAR_FRAG,
    transparent: true,
    depthWrite: false,
    blending: THREE_.AdditiveBlending,
  })
  starMat.toneMapped = false
  let stars: THREE.Points | null = null
  let starCount = 0

  let loading = false
  let ready = false
  let disposed = false
  const fetcher = opts.fetcher ?? (typeof fetch === 'function' ? fetch.bind(globalThis) : undefined)

  function load(): void {
    if (loading) return
    loading = true
    new THREE_.TextureLoader().load(
      opts.starmapUrl ?? STARMAP_URL,
      (tex) => {
        if (disposed) {
          tex.dispose()
          return
        }
        tex.colorSpace = THREE_.SRGBColorSpace
        // Near 1:1 with the eye at 4096 px around: no mipmaps, so the seam
        // where the map wraps draws no line.
        tex.generateMipmaps = false
        tex.minFilter = THREE_.LinearFilter
        tex.magFilter = THREE_.LinearFilter
        tex.wrapS = THREE_.RepeatWrapping
        tex.wrapT = THREE_.ClampToEdgeWrapping
        skyUniforms.uMap.value = tex
        skyUniforms.uHasMap.value = 1
        ready = true
      },
      undefined,
      () => {
        // No map: still a sky — black, with the stars and the Sun.
        if (!disposed) ready = true
      },
    )
    if (!fetcher) return
    void fetcher(opts.starsUrl ?? STARS_URL)
      .then((r) => (r.ok ? (r.json() as Promise<StarsFile>) : Promise.reject(new Error(String(r.status)))))
      .then((file) => {
        if (disposed || !Array.isArray(file?.stars)) return
        buildStars(file.stars)
      })
      .catch(() => {})
  }

  function buildStars(rows: StarRow[]): void {
    const n = rows.length
    const pos = new Float32Array(n * 3)
    const mag = new Float32Array(n)
    const col = new Float32Array(n * 3)
    const DEG = Math.PI / 180
    const r = NIGHT_SKY_M - 1
    for (let i = 0; i < n; i++) {
      const [raDeg, decDeg, vmag, bv] = rows[i]
      const d = eciDirection(raDeg * DEG, decDeg * DEG)
      pos[i * 3] = d[0] * r
      pos[i * 3 + 1] = d[1] * r
      pos[i * 3 + 2] = d[2] * r
      mag[i] = vmag
      const c = starColour(bv)
      col[i * 3] = c[0]
      col[i * 3 + 1] = c[1]
      col[i * 3 + 2] = c[2]
    }
    const geo = new THREE_.BufferGeometry()
    geo.setAttribute('position', new THREE_.BufferAttribute(pos, 3))
    geo.setAttribute('aMag', new THREE_.BufferAttribute(mag, 1))
    geo.setAttribute('aColor', new THREE_.BufferAttribute(col, 3))
    stars = new THREE_.Points(geo, starMat)
    stars.frustumCulled = false
    stars.renderOrder = -9
    group.add(stars)
    starCount = n
  }

  const m4 = new THREE_.Matrix4()
  let matrix: readonly number[] | null = null

  return {
    group,
    get ready() {
      return ready
    },
    get starCount() {
      return starCount
    },

    update(on, celestialToRoom, timeMs, viewportHeightPx) {
      if (on && !loading) load()
      const show = on && ready && !!celestialToRoom
      group.visible = show
      if (!show || !celestialToRoom) {
        matrix = null
        return
      }
      const m = celestialToRoom
      matrix = m
      m4.set(m[0], m[1], m[2], 0, m[3], m[4], m[5], 0, m[6], m[7], m[8], 0, 0, 0, 0, 1)
      group.matrix.copy(m4)
      group.matrixWorldNeedsUpdate = true
      // The Sun, in the sky's own (inertial) frame.
      const { ra, dec } = sunRaDec(timeMs)
      const s = eciDirection(ra, dec)
      skyUniforms.uSun.value.set(s[0], s[1], s[2])
      starUniforms.uScale.value = Math.max(0.8, viewportHeightPx / 1100)
    },

    domeDirection(raRad, decRad) {
      if (!matrix) return null
      return apply3(matrix, eciDirection(raRad, decRad))
    },

    dispose() {
      disposed = true
      skyUniforms.uMap.value?.dispose()
      skyMat.dispose()
      skyGeo.dispose()
      if (stars) stars.geometry.dispose()
      starMat.dispose()
      parent.remove(group)
    },
  }
}
